/**
 * VenueFlow Premium App Shell
 * 
 * A stunning, polished application shell with:
 * - Premium dark sidebar with glass-morphism effects
 * - Animated gradient accents
 * - Smooth transitions and micro-interactions
 * - Club-branded header
 * - Professional typography
 * - Collapsible sidebar for MatchFlow integration
 * - Supabase authentication with magic links
 * - Enhanced welcome dashboard with stats, tips, and support contacts
 * - Maintenance mode for Lumen8 operators
 */

import React, { useState, useCallback, useEffect, useRef, createContext, useContext } from "react";
import { VFContextProvider, useVFContext } from "./context/VFContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { PresenceProvider } from "./context/PresenceContext";
import { isSupabaseConfigured, supabase } from "./lib/supabase";
import LoginPage from "./pages/LoginPage";
import ProfileCompleteModal from "./components/auth/ProfileCompleteModal";
import ChangePasswordModal from "./components/auth/ChangePasswordModal";
import UserSettingsModal from "./components/ui/UserSettingsModal";
import WelcomeTour from "./components/ui/WelcomeTour";
import HelpWidget from "./components/ui/HelpWidget";
import ChatWidget from "./components/ui/ChatWidget";
import { WelcomeDashboard, WelcomeHero } from "./components/ui/WelcomeWidgets";

// Import pages
import MCGIPTVPage from "./pages/Uploaders/MCG/IPTV";
import MCGLEDPage from "./pages/Uploaders/MCG/LED";
import MarvelLEDPage from "./pages/Uploaders/Marvel/LED";
import MarvelIPTVPage from "./pages/Uploaders/Marvel/IPTV";
import { MCGBigScreenPage, MarvelScreensPage } from "./pages/Uploaders/Screens";
import { MCGTVCPage, MarvelTVCPage } from "./pages/Uploaders/TVC";
import MCGCommercialLEDPage from "./pages/Uploaders/Commercial/MCGLED";
import MarvelCommercialLEDPage from "./pages/Uploaders/Commercial/MarvelLED";
import MCGCommercialIPTVPage from "./pages/Uploaders/Commercial/MCGIPTV";
import MarvelCommercialIPTVPage from "./pages/Uploaders/Commercial/MarvelIPTV";
import IngestFlowGalleryPage from "./pages/IngestFlowGallery";

// ControlFlow pages
import { OperatorDashboard } from "./pages/Operator";
import AdminPage from "./pages/AdminPage";
import CommercialQAView from "./pages/CommercialQAView";
import PortalHub from "./pages/PortalHub";
import { OperatorContextProvider } from "./context/OperatorContext";
import HubPage from "./pages/HubPage";

// Import MatchFlow (App.js) - completely unchanged
import MatchFlowApp from "./App";
import UpdateBanner from "./components/UpdateBanner";

// ============================================================
// MAINTENANCE MODE CONTEXT
// ============================================================
const MaintenanceContext = createContext({
  isMaintenanceMode: false,
  maintenanceMessage: '',
  toggleMaintenance: () => {},
  setMaintenanceMessage: () => {},
});

export const useMaintenanceMode = () => useContext(MaintenanceContext);

// ============================================================
// MATCH LOCK CONTEXT — Per-match mode locking
// ============================================================
const CLUB_CODE = (process.env.REACT_APP_DEFAULT_CLUB || '').toUpperCase();

// Mode keys that can be locked (matches ModeSelector tabs)
const LOCKABLE_MODES = [
  { key: 'out', label: 'Game Timeline' },
  { key: 'team', label: 'Team List' },
  { key: 'iptv', label: 'IPTV' },
  { key: 'tvc', label: 'TVC Playlist' },
  { key: 'commled', label: 'In-Game LED Playlist' },
];

const MatchLockContext = createContext({
  matchLocks: {},        // { [viewName]: { out: true, team: false, ... } }
  isModeLocked: () => false,
  toggleModeLock: () => {},
  getLocksForView: () => ({}),
  lockableViews: [],     // Available fixture views to lock
  setLockableViews: () => {},
});

export const useMatchLock = () => useContext(MatchLockContext);

function MatchLockProvider({ children }) {
  const [matchLocks, setMatchLocks] = useState({});
  const [lockableViews, setLockableViews] = useState([]);
  const channelRef = useRef(null);
  const pollRef = useRef(null);

  const settingsKey = `match_locks_${CLUB_CODE}`;

  // ── Load initial state from Supabase ──
  const fetchLocks = useCallback(async () => {
    if (!isSupabaseConfigured() || !CLUB_CODE) return;
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', settingsKey)
        .maybeSingle();

      if (!error && data) {
        const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        if (parsed && typeof parsed === 'object') {
          setMatchLocks(parsed);
        }
      }
    } catch (err) {
      console.warn('[MatchLock] Failed to fetch:', err);
    }
  }, [settingsKey]);

  // ── Initial load + polling ──
  useEffect(() => {
    fetchLocks();
    pollRef.current = setInterval(fetchLocks, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchLocks]);

  // ── Realtime broadcast listener ──
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const channel = supabase.channel(`match-locks-${CLUB_CODE}`)
      .on('broadcast', { event: 'match_lock_update' }, (payload) => {
        const locks = payload.payload?.locks;
        if (locks && typeof locks === 'object') {
          setMatchLocks(locks);
        }
      })
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, []);

  // ── Check if a specific mode is locked for a given view ──
  const isModeLocked = useCallback((viewName, modeKey) => {
    if (!viewName || !modeKey) return false;
    const viewLocks = matchLocks[viewName];
    if (!viewLocks || !viewLocks.locks) return false;
    return !!viewLocks.locks[modeKey];
  }, [matchLocks]);

  // ── Get all locks for a specific view ──
  const getLocksForView = useCallback((viewName) => {
    if (!viewName) return {};
    return matchLocks[viewName]?.locks || {};
  }, [matchLocks]);

  // ── Toggle a mode lock for a specific view ──
  const toggleModeLock = useCallback(async (viewName, modeKey, lockedBy) => {
    if (!viewName || !modeKey) return;

    const newMatchLocks = { ...matchLocks };
    if (!newMatchLocks[viewName]) {
      newMatchLocks[viewName] = { locks: {}, lockedBy: '', lockedAt: '' };
    }
    const currentValue = !!newMatchLocks[viewName].locks[modeKey];
    newMatchLocks[viewName] = {
      ...newMatchLocks[viewName],
      locks: { ...newMatchLocks[viewName].locks, [modeKey]: !currentValue },
      lockedBy: lockedBy || '',
      lockedAt: new Date().toISOString(),
    };
    setMatchLocks(newMatchLocks);

    if (isSupabaseConfigured()) {
      try {
        await supabase
          .from('app_settings')
          .upsert({
            key: settingsKey,
            value: newMatchLocks,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'key' });

        const channel = supabase.channel(`match-locks-${CLUB_CODE}`);
        await channel.send({
          type: 'broadcast',
          event: 'match_lock_update',
          payload: { locks: newMatchLocks },
        });
      } catch (err) {
        console.error('[MatchLock] Failed to save:', err);
      }
    }
  }, [matchLocks, settingsKey]);

  // ── Bulk set all modes for a view ──
  const setAllModeLocks = useCallback(async (viewName, lockAll, lockedBy) => {
    if (!viewName) return;
    const newLocks = {};
    LOCKABLE_MODES.forEach(m => { newLocks[m.key] = lockAll; });
    const newMatchLocks = {
      ...matchLocks,
      [viewName]: { locks: newLocks, lockedBy: lockedBy || '', lockedAt: new Date().toISOString() },
    };
    setMatchLocks(newMatchLocks);

    if (isSupabaseConfigured()) {
      try {
        await supabase
          .from('app_settings')
          .upsert({
            key: settingsKey,
            value: newMatchLocks,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'key' });

        const channel = supabase.channel(`match-locks-${CLUB_CODE}`);
        await channel.send({
          type: 'broadcast',
          event: 'match_lock_update',
          payload: { locks: newMatchLocks },
        });
      } catch (err) {
        console.error('[MatchLock] Failed to save:', err);
      }
    }
  }, [matchLocks, settingsKey]);

  return (
    <MatchLockContext.Provider value={{
      matchLocks,
      isModeLocked,
      toggleModeLock,
      getLocksForView,
      lockableViews,
      setLockableViews,
      setAllModeLocks,
    }}>
      {children}
    </MatchLockContext.Provider>
  );
}

function MaintenanceProvider({ children }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('System maintenance in progress. Please check back shortly.');
  const channelRef = useRef(null);
  const pollRef = useRef(null);

  // ── Load initial state from Supabase ──
  const fetchMaintenanceStatus = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();
      
      if (!error && data) {
        const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        setIsMaintenanceMode(!!parsed.enabled);
        if (parsed.message) setMaintenanceMessage(parsed.message);
      }
    } catch (err) {
      console.warn('[Maintenance] Failed to fetch status:', err);
    }
  }, []);

  // ── Initial load + polling fallback ──
  useEffect(() => {
    fetchMaintenanceStatus();
    
    // Poll every 30 seconds as fallback (in case Realtime misses an update)
    pollRef.current = setInterval(fetchMaintenanceStatus, 30000);
    
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchMaintenanceStatus]);

  // ── Supabase Realtime: Listen for broadcast updates ──
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    
    const channel = supabase.channel('maintenance-status')
      .on('broadcast', { event: 'maintenance_update' }, (payload) => {
        console.log('[Maintenance] Received broadcast:', payload);
        const { enabled, message } = payload.payload || {};
        setIsMaintenanceMode(!!enabled);
        if (message) setMaintenanceMessage(message);
      })
      .subscribe();
    
    channelRef.current = channel;
    
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  // ── Toggle: Write to Supabase DB + Broadcast to all clients ──
  const toggleMaintenance = useCallback(async () => {
    const newValue = !isMaintenanceMode;
    setIsMaintenanceMode(newValue);
    
    if (isSupabaseConfigured()) {
      try {
        // Write to DB (persistent)
        await supabase
          .from('app_settings')
          .upsert({
            key: 'maintenance_mode',
            value: { enabled: newValue, message: maintenanceMessage },
            updated_at: new Date().toISOString(),
          }, { onConflict: 'key' });
        
        // Broadcast to all connected clients (instant)
        const channel = supabase.channel('maintenance-status');
        await channel.send({
          type: 'broadcast',
          event: 'maintenance_update',
          payload: { enabled: newValue, message: maintenanceMessage },
        });
        
        console.log('[Maintenance] Toggled to:', newValue);
      } catch (err) {
        console.error('[Maintenance] Failed to save:', err);
      }
    }
  }, [isMaintenanceMode, maintenanceMessage]);

  // ── Update message: Write to Supabase DB + Broadcast ──
  const updateMessage = useCallback(async (msg) => {
    setMaintenanceMessage(msg);
    
    if (isSupabaseConfigured()) {
      try {
        await supabase
          .from('app_settings')
          .upsert({
            key: 'maintenance_mode',
            value: { enabled: isMaintenanceMode, message: msg },
            updated_at: new Date().toISOString(),
          }, { onConflict: 'key' });
        
        // Broadcast message change to all clients
        const channel = supabase.channel('maintenance-status');
        await channel.send({
          type: 'broadcast',
          event: 'maintenance_update',
          payload: { enabled: isMaintenanceMode, message: msg },
        });
      } catch (err) {
        console.error('[Maintenance] Failed to save message:', err);
      }
    }
  }, [isMaintenanceMode]);

  return (
    <MaintenanceContext.Provider value={{
      isMaintenanceMode,
      maintenanceMessage,
      toggleMaintenance,
      setMaintenanceMessage: updateMessage,
    }}>
      {children}
    </MaintenanceContext.Provider>
  );
}

// ============================================================
// GLOBAL STYLES (injected once)
// ============================================================
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #0a0a0f;
    color: #fff;
    overflow: hidden;
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.25);
  }
  
  /* Animated gradient keyframes */
  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  @keyframes slideIn {
    from { 
      opacity: 0; 
      transform: translateX(-10px); 
    }
    to { 
      opacity: 1; 
      transform: translateX(0); 
    }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideOut {
    from { 
      transform: translateX(0);
      opacity: 1;
    }
    to { 
      transform: translateX(-100%);
      opacity: 0;
    }
  }
  
  .nav-item-enter {
    animation: slideIn 0.3s ease forwards;
  }
  
  .page-enter {
    animation: fadeIn 0.25s ease forwards;
  }
  
  /* Sidebar collapse transition */
  .sidebar-expanded {
    width: 280px;
    min-width: 280px;
  }
  
  .sidebar-collapsed {
    width: 0;
    min-width: 0;
    overflow: hidden;
    border-right: none;
  }
  
  .sidebar-transition {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.2s ease,
                box-shadow 0.3s ease;
  }
`;

// Inject global styles
if (typeof document !== "undefined" && !document.getElementById("vf-global-styles")) {
  const style = document.createElement("style");
  style.id = "vf-global-styles";
  style.textContent = globalStyles;
  document.head.appendChild(style);
}

// ============================================================
// THEME
// ============================================================
const THEME = {
  // Core colors
  bg: "#0a0a0f",
  bgElevated: "#12121a",
  bgCard: "#16161f",
  bgHover: "#1e1e2a",
  
  // Accent - Lumen8 teal
  accent: "#2fd6b5",
  accentHover: "#26b89a",
  accentGlow: "rgba(47, 214, 181, 0.15)",
  accentGlowStrong: "rgba(47, 214, 181, 0.3)",
  
  // Secondary accent - lime
  lime: "#cdde25",
  limeGlow: "rgba(205, 222, 37, 0.15)",
  
  // Text
  textPrimary: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  textDim: "#52525b",
  
  // Borders
  border: "rgba(255, 255, 255, 0.08)",
  borderHover: "rgba(255, 255, 255, 0.15)",
  borderAccent: "rgba(47, 214, 181, 0.3)",
  
  // Gradients
  gradientAccent: "linear-gradient(135deg, #2fd6b5 0%, #cdde25 100%)",
  gradientDark: "linear-gradient(180deg, #16161f 0%, #0a0a0f 100%)",
  gradientGlow: "radial-gradient(ellipse at top, rgba(47, 214, 181, 0.08) 0%, transparent 60%)",
  
  // Shadows
  shadowSm: "0 2px 8px rgba(0, 0, 0, 0.3)",
  shadowMd: "0 4px 16px rgba(0, 0, 0, 0.4)",
  shadowLg: "0 8px 32px rgba(0, 0, 0, 0.5)",
  shadowGlow: "0 0 30px rgba(47, 214, 181, 0.15)",
  
  // Transitions
  transitionFast: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
  transitionMedium: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  transitionSlow: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
};

// ============================================================
// NAVIGATION STRUCTURE
// ============================================================
const NAV_ITEMS = [
  {
    id: "upload-content",
    label: "Upload Content",
    icon: "upload",
    tourId: "upload", // For welcome tour targeting
    children: [
      {
        id: "marvel-uploads",
        label: "Marvel Stadium",
        icon: "stadium",
        children: [
          { id: "marvel-screens", label: "Corner & Superscreens", path: "/uploads/marvel/screens" },
          { id: "marvel-led", label: "LED Ribbons", path: "/uploads/marvel/led-ribbons" },
          { id: "marvel-iptv", label: "IPTV", path: "/uploads/marvel/iptv" },
          { id: "marvel-tvc", label: "TVC & Highlights", path: "/uploads/marvel/tvc" },
          { id: "commercial-marvel-led", label: "Commercial LED", path: "/uploads/commercial/marvel-led" },
          { id: "commercial-marvel-iptv", label: "Commercial IPTV", path: "/uploads/commercial/marvel-iptv" },
        ],
      },
      {
        id: "mcg-uploads",
        label: "MCG",
        icon: "stadium",
        children: [
          { id: "mcg-bigscreen", label: "Big Screen", path: "/uploads/mcg/bigscreen" },
          { id: "mcg-led", label: "LED Ribbons", path: "/uploads/mcg/led-ribbons" },
          { id: "mcg-iptv", label: "IPTV", path: "/uploads/mcg/iptv" },
          { id: "mcg-tvc", label: "TVC & Highlights", path: "/uploads/mcg/tvc" },
          { id: "commercial-mcg-led", label: "Commercial LED", path: "/uploads/commercial/mcg-led" },
          { id: "commercial-mcg-iptv", label: "Commercial IPTV", path: "/uploads/commercial/mcg-iptv" },
        ],
      },
    ],
  },
  { type: "divider" },
  {
    id: "matchflow-gallery",
    label: "MatchFlow Library",
    icon: "gallery",
    path: "/ingestflow",
    tourId: "gallery", // For welcome tour targeting
  },
  {
    id: "matchflow",
    label: "MatchFlow Builder",
    icon: "play",
    path: "/matchflow",
    tourId: "matchflow", // For welcome tour targeting
  },
];

// Operator-only navigation items (shown only to Lumen8 staff)
const OPERATOR_NAV_ITEMS = [
  { type: "divider" },
  {
    id: "operator-portal",
    label: "Operator View",
    icon: "operator",
    operatorOnly: true,
    path: "/operator/cg",
  },
  {
    id: "admin-tools",
    label: "Password Manager",
    icon: "lock",
    operatorOnly: true,
    path: "/admin",
  },
];

// Producer nav: uploaders + asset library, no MatchFlow Builder or operator tools
const PRODUCER_NAV_ITEMS = NAV_ITEMS.filter(
  (item) => item.id === "upload-content" || item.id === "matchflow-gallery"
);

// ============================================================
// ICONS
// ============================================================
const Icons = {
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  stadium: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="12" rx="10" ry="4" />
      <path d="M2 12v4c0 2.21 4.48 4 10 4s10-1.79 10-4v-4" />
      <path d="M2 8v4" />
      <path d="M22 8v4" />
      <ellipse cx="12" cy="8" rx="10" ry="4" />
    </svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  gallery: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  play: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" opacity="0.2" />
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  specs: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  arrowLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  operator: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      <circle cx="12" cy="16" r="1" />
    </svg>
  ),
  hub: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  shell: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    background: THEME.bg,
  },
  
  // Sidebar (stays dark in both themes for brand consistency)
  sidebar: {
    width: 280,
    minWidth: 280,
    height: "100vh",
    background: THEME.bgElevated,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${THEME.border}`,
    position: "relative",
    zIndex: 100,
    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  sidebarCollapsed: {
    width: 0,
    minWidth: 0,
    padding: 0,
    overflow: "hidden",
    borderRight: "none",
  },
  sidebarGlow: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 200,
    background: THEME.gradientGlow,
    pointerEvents: "none",
  },
  
  // Logo section
  logoSection: {
    padding: "20px 20px 20px",
    position: "relative",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
  logoImage: {
    height: 28,
    width: "auto",
    objectFit: "contain",
  },
  logoSub: {
    fontSize: 11,
    fontWeight: 600,
    background: "linear-gradient(135deg, rgba(47, 214, 181, 0.7) 0%, rgba(132, 204, 22, 0.7) 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "4px",
    textTransform: "uppercase",
    marginTop: 8,
    marginLeft: 2,
  },
  
  // Club badge
  clubBadge: {
    marginTop: 16,
    padding: "10px 14px",
    background: THEME.bgCard,
    borderRadius: 10,
    border: `1px solid ${THEME.border}`,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  clubLogo: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: THEME.accentGlow,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: THEME.accent,
    fontFamily: "'JetBrains Mono', monospace",
  },
  clubInfo: {
    flex: 1,
  },
  clubName: {
    fontSize: 13,
    fontWeight: 600,
    color: THEME.textPrimary,
  },
  clubType: {
    fontSize: 11,
    color: THEME.textMuted,
  },
  
  // Navigation
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 12px",
  },
  navSection: {
    marginBottom: 2,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    padding: "11px 12px",
    borderRadius: 8,
    color: THEME.textSecondary,
    cursor: "pointer",
    transition: THEME.transitionFast,
    fontSize: 14,
    fontWeight: 500,
    gap: 12,
    position: "relative",
    userSelect: "none",
  },
  navItemHover: {
    background: THEME.accentGlow,
    color: THEME.accent,
  },
  navItemActive: {
    background: THEME.accentGlow,
    color: THEME.accent,
    borderLeft: `3px solid ${THEME.accent}`,
    marginLeft: -3,
  },
  navItemActiveParent: {
    color: THEME.accent,
  },
  navIcon: {
    width: 20,
    height: 20,
    opacity: 0.8,
    flexShrink: 0,
  },
  navLabel: {
    flex: 1,
  },
  chevron: {
    width: 16,
    height: 16,
    opacity: 0.4,
    transition: THEME.transitionFast,
    flexShrink: 0,
  },
  chevronExpanded: {
    transform: "rotate(90deg)",
    opacity: 0.7,
  },
  
  // Nav children
  navChildren: {
    overflow: "hidden",
    transition: THEME.transitionMedium,
  },
  navChild: {
    display: "flex",
    alignItems: "center",
    padding: "9px 12px 9px 44px",
    borderRadius: 6,
    color: THEME.textMuted,
    cursor: "pointer",
    transition: THEME.transitionFast,
    fontSize: 13,
    fontWeight: 400,
  },
  navChildHover: {
    background: THEME.accentGlow,
    color: THEME.accent,
  },
  navChildActive: {
    color: THEME.accent,
    background: THEME.accentGlow,
    fontWeight: 500,
  },
  navChildDot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "currentColor",
    marginRight: 12,
    opacity: 0.5,
  },
  
  // Divider
  divider: {
    height: 1,
    background: THEME.border,
    margin: "12px 12px",
  },
  
  // Footer
  footer: {
    padding: "12px",
    borderTop: `1px solid ${THEME.border}`,
  },
  specsButton: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    background: "transparent",
    border: `1px solid ${THEME.border}`,
    color: THEME.textMuted,
    cursor: "pointer",
    width: "100%",
    fontSize: 13,
    fontWeight: 500,
    transition: THEME.transitionFast,
    fontFamily: "inherit",
  },
  specsButtonHover: {
    background: THEME.accentGlow,
    borderColor: THEME.borderAccent,
    color: THEME.accent,
  },
  
  // User section
  userSection: {
    padding: "16px 16px",
    borderTop: `1px solid ${THEME.border}`,
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    transition: THEME.transitionFast,
  },
  userSectionHover: {
    background: THEME.accentGlow,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: THEME.gradientAccent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: THEME.bg,
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: THEME.textPrimary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: 12,
    color: THEME.textMuted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  
  // Main content
  main: {
    flex: 1,
    overflow: "auto",
    background: THEME.bg,
    position: "relative",
  },
  mainContent: {
    minHeight: "100%",
  },
  
  // Floating back button (when sidebar is collapsed)
  floatingBackButton: {
    position: "fixed",
    top: 16,
    left: 16,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    background: THEME.bgElevated,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    color: THEME.textSecondary,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "'Space Grotesk', sans-serif",
    transition: THEME.transitionFast,
    boxShadow: THEME.shadowMd,
  },
  floatingBackButtonHover: {
    background: THEME.bgHover,
    borderColor: THEME.borderAccent,
    color: THEME.accent,
    boxShadow: THEME.shadowGlow,
  },
  
  // Welcome screen (base styles for cards)
  welcomeCard: {
    background: THEME.bgCard,
    border: `1px solid ${THEME.border}`,
    borderRadius: 16,
    padding: 24,
    textAlign: "left",
    cursor: "pointer",
    transition: THEME.transitionMedium,
  },
  welcomeCardHover: {
    background: THEME.bgHover,
    borderColor: THEME.borderAccent,
    transform: "translateY(-4px)",
    boxShadow: THEME.shadowGlow,
  },
  welcomeCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: THEME.accentGlow,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    color: THEME.accent,
  },
  welcomeCardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.textPrimary,
    marginBottom: 6,
  },
  welcomeCardDesc: {
    fontSize: 13,
    color: THEME.textMuted,
    lineHeight: 1.5,
  },
};

// ============================================================
// MAINTENANCE MODE OVERLAY
// ============================================================
function MaintenanceOverlay() {
  const { maintenanceMessage } = useMaintenanceMode();
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(10, 10, 15, 0.98)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: 40,
    }}>
      {/* Animated background glow */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        height: 600,
        background: 'radial-gradient(circle, rgba(47, 214, 181, 0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
        animation: 'pulse 4s ease-in-out infinite',
      }} />
      
      {/* Content */}
      <div style={{
        position: 'relative',
        textAlign: 'center',
        maxWidth: 500,
      }}>
        {/* Icon */}
        <div style={{
          width: 80,
          height: 80,
          borderRadius: 20,
          background: 'rgba(47, 214, 181, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          border: '1px solid rgba(47, 214, 181, 0.3)',
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2fd6b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        
        {/* Title */}
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#fff',
          marginBottom: 16,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          Down for Maintenance
        </h1>
        
        {/* Message */}
        <p style={{
          fontSize: 16,
          color: 'rgba(255, 255, 255, 0.7)',
          lineHeight: 1.6,
          marginBottom: 32,
        }}>
          {maintenanceMessage}
        </p>
        
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          opacity: 0.6,
        }}>
          <img 
            src="/lumen8-logo.png" 
            alt="Lumen8" 
            style={{ height: 24 }}
          />
          <span style={{ 
            fontSize: 14, 
            color: 'rgba(255, 255, 255, 0.5)',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            VenueFlow
          </span>
        </div>
      </div>
      
      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// MAINTENANCE TOGGLE (for sidebar)
// ============================================================
function MaintenanceToggle() {
  const { isMaintenanceMode, toggleMaintenance, maintenanceMessage, setMaintenanceMessage } = useMaintenanceMode();
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [tempMessage, setTempMessage] = useState(maintenanceMessage);
  const [hovered, setHovered] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const handleSaveMessage = () => {
    setMaintenanceMessage(tempMessage);
    setShowMessageInput(false);
  };

  const handleToggleClick = () => {
    if (isMaintenanceMode) {
      // Turning OFF — no confirm needed
      toggleMaintenance();
    } else {
      // Turning ON — show confirmation modal
      setShowConfirm(true);
    }
  };

  const handleConfirmEnable = () => {
    setShowConfirm(false);
    toggleMaintenance();
  };
  
  return (
    <div style={{ padding: '8px 16px', borderTop: `1px solid ${THEME.border}` }}>
      {/* Confirmation Modal */}
      {showConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowConfirm(false)}>
          <div style={{
            background: '#1a1a24', borderRadius: 12, padding: '24px 28px',
            border: '1px solid rgba(239, 68, 68, 0.3)', maxWidth: 400, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 20 }}>🔧</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#ef4444' }}>Enable Maintenance Mode?</span>
            </div>
            <div style={{ fontSize: 12, color: '#999', lineHeight: 1.6, marginBottom: 20 }}>
              This will show a maintenance page to <strong style={{ color: '#fff' }}>all club users across every club</strong>. 
              They won't be able to access the portal until maintenance mode is turned off.
              <br /><br />
              Lumen8 operators will still have full access.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConfirm(false)} style={{
                padding: '8px 16px', borderRadius: 6, border: `1px solid ${THEME.border}`,
                background: 'transparent', color: THEME.textMuted, fontSize: 12, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleConfirmEnable} style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>Enable Maintenance</button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: isMaintenanceMode ? 'rgba(239, 68, 68, 0.15)' : THEME.bgElevated,
          border: `1px solid ${isMaintenanceMode ? 'rgba(239, 68, 68, 0.3)' : THEME.border}`,
          borderRadius: 8,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          ...(hovered && !isMaintenanceMode ? { background: THEME.bgHover, borderColor: THEME.borderAccent } : {}),
        }}
        onClick={handleToggleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isMaintenanceMode ? '#ef4444' : THEME.textMuted} strokeWidth="2">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
          <span style={{ 
            fontSize: 12, 
            fontWeight: 500,
            color: isMaintenanceMode ? '#ef4444' : THEME.textSecondary,
          }}>
            Maintenance Mode
          </span>
        </div>
        
        {/* Toggle Switch */}
        <div style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          background: isMaintenanceMode ? '#ef4444' : 'rgba(255,255,255,0.1)',
          position: 'relative',
          transition: 'background 0.2s ease',
        }}>
          <div style={{
            position: 'absolute',
            top: 2,
            left: isMaintenanceMode ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }} />
        </div>
      </div>
      
      {/* Status indicator */}
      {isMaintenanceMode && (
        <div style={{
          marginTop: 8,
          padding: '8px 12px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: 6,
          fontSize: 11,
          color: '#ef4444',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#ef4444',
            animation: 'blink 1s infinite',
          }} />
          Site is showing maintenance page
        </div>
      )}
      
      {/* Edit message button */}
      {isMaintenanceMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setTempMessage(maintenanceMessage);
            setShowMessageInput(!showMessageInput);
          }}
          style={{
            marginTop: 8,
            width: '100%',
            padding: '6px 10px',
            background: 'transparent',
            border: `1px solid ${THEME.border}`,
            borderRadius: 6,
            color: THEME.textMuted,
            fontSize: 11,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          ✏️ Edit maintenance message
        </button>
      )}
      
      {/* Message input */}
      {showMessageInput && (
        <div style={{ marginTop: 8 }}>
          <textarea
            value={tempMessage}
            onChange={(e) => setTempMessage(e.target.value)}
            style={{
              width: '100%',
              padding: 10,
              background: THEME.bgElevated,
              border: `1px solid ${THEME.border}`,
              borderRadius: 6,
              color: THEME.textPrimary,
              fontSize: 12,
              resize: 'vertical',
              minHeight: 60,
              fontFamily: "'Space Grotesk', sans-serif",
            }}
            placeholder="Enter maintenance message..."
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={handleSaveMessage}
              style={{
                flex: 1,
                padding: '6px 12px',
                background: THEME.accent,
                border: 'none',
                borderRadius: 6,
                color: '#000',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Save
            </button>
            <button
              onClick={() => setShowMessageInput(false)}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: `1px solid ${THEME.border}`,
                borderRadius: 6,
                color: THEME.textMuted,
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// MATCH LOCK TOGGLE — Sidebar component (Lumen8 only)
// ============================================================
function MatchLockToggle() {
  const { matchLocks, toggleModeLock, getLocksForView, lockableViews, setAllModeLocks } = useMatchLock();
  const { userEmail } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedView, setSelectedView] = useState('');
  const [hovered, setHovered] = useState(false);

  // Count total locked modes across all views
  const totalLockedModes = Object.values(matchLocks).reduce((sum, viewData) => {
    if (!viewData?.locks) return sum;
    return sum + Object.values(viewData.locks).filter(Boolean).length;
  }, 0);

  const handleOpenModal = () => {
    // Default to first view if none selected
    if (!selectedView && lockableViews.length > 0) {
      setSelectedView(lockableViews[0]);
    }
    setShowModal(true);
  };

  const currentViewLocks = selectedView ? getLocksForView(selectedView) : {};
  const allLocked = LOCKABLE_MODES.every(m => !!currentViewLocks[m.key]);
  const anyLocked = LOCKABLE_MODES.some(m => !!currentViewLocks[m.key]);

  return (
    <div style={{ padding: '8px 16px', borderTop: `1px solid ${THEME.border}` }}>
      {/* Lock Match Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: '#1a1a24', borderRadius: 12, padding: '24px 28px',
            border: '1px solid rgba(251, 191, 36, 0.3)', maxWidth: 440, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 20 }}>🔒</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fbbf24' }}>Lock Match Tables</span>
            </div>
            <div style={{ fontSize: 12, color: '#999', lineHeight: 1.6, marginBottom: 16 }}>
              Lock specific tables for a match to prevent clubs from editing or submitting new content.
              Locked tables will show a message directing the club to contact their Lumen8 coordinator.
            </div>

            {/* Match Selector */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                SELECT MATCH
              </label>
              {lockableViews.length === 0 ? (
                <div style={{ fontSize: 12, color: '#666', padding: '10px 0' }}>
                  No matches loaded. Open MatchFlow Builder first to load available matches.
                </div>
              ) : (
                <select
                  value={selectedView}
                  onChange={(e) => setSelectedView(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#12121a',
                    border: `1px solid ${THEME.border}`,
                    borderRadius: 6,
                    color: '#fff',
                    fontSize: 13,
                    fontFamily: "'Space Grotesk', sans-serif",
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  {lockableViews.map(v => (
                    <option key={v} value={v} style={{ color: '#000' }}>{v}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Mode Toggles */}
            {selectedView && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>
                    TABLES TO LOCK
                  </label>
                  <button
                    onClick={() => setAllModeLocks(selectedView, !allLocked, userEmail)}
                    style={{
                      padding: '4px 10px',
                      background: allLocked ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.15)',
                      border: `1px solid ${allLocked ? 'rgba(239, 68, 68, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
                      borderRadius: 4,
                      color: allLocked ? '#ef4444' : '#fbbf24',
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {allLocked ? 'Unlock All' : 'Lock All'}
                  </button>
                </div>

                {LOCKABLE_MODES.map(mode => {
                  const isLocked = !!currentViewLocks[mode.key];
                  return (
                    <div
                      key={mode.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: isLocked ? 'rgba(251, 191, 36, 0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isLocked ? 'rgba(251, 191, 36, 0.2)' : THEME.border}`,
                        borderRadius: 6,
                        marginBottom: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => toggleModeLock(selectedView, mode.key, userEmail)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14 }}>{isLocked ? '🔒' : '🔓'}</span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: isLocked ? '#fbbf24' : '#ccc' }}>
                          {mode.label}
                        </span>
                      </div>

                      {/* Toggle Switch */}
                      <div style={{
                        width: 36, height: 20, borderRadius: 10,
                        background: isLocked ? '#fbbf24' : 'rgba(255,255,255,0.1)',
                        position: 'relative', transition: 'background 0.2s ease',
                      }}>
                        <div style={{
                          position: 'absolute', top: 2,
                          left: isLocked ? 18 : 2,
                          width: 16, height: 16, borderRadius: '50%',
                          background: '#fff',
                          transition: 'left 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Lock info */}
            {selectedView && matchLocks[selectedView]?.lockedBy && (
              <div style={{
                fontSize: 11, color: '#666', padding: '8px 0', borderTop: `1px solid ${THEME.border}`,
              }}>
                Last changed by <span style={{ color: '#999' }}>{matchLocks[selectedView].lockedBy}</span>
                {matchLocks[selectedView].lockedAt && (
                  <span> · {new Date(matchLocks[selectedView].lockedAt).toLocaleString()}</span>
                )}
              </div>
            )}

            {/* Close */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setShowModal(false)} style={{
                padding: '8px 20px', borderRadius: 6, border: `1px solid ${THEME.border}`,
                background: 'transparent', color: THEME.textMuted, fontSize: 12, cursor: 'pointer',
              }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Button */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px',
          background: anyLocked ? 'rgba(251, 191, 36, 0.12)' : THEME.bgElevated,
          border: `1px solid ${anyLocked ? 'rgba(251, 191, 36, 0.25)' : THEME.border}`,
          borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s ease',
          ...(hovered && !anyLocked ? { background: THEME.bgHover, borderColor: THEME.borderAccent } : {}),
        }}
        onClick={handleOpenModal}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={anyLocked ? '#fbbf24' : THEME.textMuted} strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span style={{
            fontSize: 12, fontWeight: 500,
            color: anyLocked ? '#fbbf24' : THEME.textSecondary,
          }}>
            Lock Match
          </span>
        </div>

        {/* Badge showing locked count */}
        {totalLockedModes > 0 && (
          <span style={{
            background: '#fbbf24', color: '#000',
            fontSize: 10, fontWeight: 700,
            padding: '2px 7px', borderRadius: 10,
            lineHeight: 1.4,
          }}>
            {totalLockedModes}
          </span>
        )}
      </div>

      {/* Active locks summary */}
      {anyLocked && (
        <div style={{
          marginTop: 8, padding: '8px 12px',
          background: 'rgba(251, 191, 36, 0.08)',
          borderRadius: 6, fontSize: 11, color: '#fbbf24',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#fbbf24', animation: 'blink 1s infinite',
          }} />
          {totalLockedModes} table{totalLockedModes !== 1 ? 's' : ''} locked
        </div>
      )}
    </div>
  );
}

function NavItem({ item, currentPath, onNavigate, expanded, onToggle, expandedSections, level = 0 }) {
  const [hovered, setHovered] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path === currentPath;
  
  // Check if any descendant is active (for nested structures)
  const hasActiveDescendant = (children) => {
    if (!children) return false;
    return children.some(child => 
      child.path === currentPath || hasActiveDescendant(child.children)
    );
  };
  const hasActiveChild = hasActiveDescendant(item.children);

  const handleClick = () => {
    if (hasChildren) {
      onToggle(item.id);
    } else if (item.path) {
      onNavigate(item.path);
    }
  };

  // Calculate max height for nested children
  const calcMaxHeight = (children) => {
    if (!children) return 0;
    let height = 0;
    children.forEach(child => {
      height += 42; // Base height per item
      if (child.children && expandedSections?.includes(child.id)) {
        height += calcMaxHeight(child.children);
      }
    });
    return height;
  };

  return (
    <div style={styles.navSection}>
      <div
        data-tour={item.tourId || undefined}
        style={{
          ...styles.navItem,
          ...(hovered ? styles.navItemHover : {}),
          ...(isActive ? styles.navItemActive : {}),
          ...(hasActiveChild && !isActive ? styles.navItemActiveParent : {}),
          paddingLeft: level > 0 ? 20 + (level * 12) : 16,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
      >
        {item.icon && <span style={styles.navIcon}>{Icons[item.icon]}</span>}
        <span style={styles.navLabel}>{item.label}</span>
        {hasChildren && (
          <span style={{
            ...styles.chevron,
            ...(expanded ? styles.chevronExpanded : {}),
          }}>
            {Icons.chevron}
          </span>
        )}
      </div>

      {hasChildren && (
        <div style={{
          ...styles.navChildren,
          maxHeight: expanded ? `${calcMaxHeight(item.children) + 20}px` : 0,
        }}>
          {item.children.map((child, idx) => {
            // If child has its own children, render as nested NavItem
            if (child.children && child.children.length > 0) {
              return (
                <NavItem
                  key={child.id}
                  item={child}
                  currentPath={currentPath}
                  onNavigate={onNavigate}
                  expanded={expandedSections?.includes(child.id)}
                  onToggle={onToggle}
                  expandedSections={expandedSections}
                  level={level + 1}
                />
              );
            }
            // Otherwise render as NavChild (leaf node)
            return (
              <NavChild
                key={child.id}
                item={child}
                currentPath={currentPath}
                onNavigate={onNavigate}
                delay={idx * 30}
                level={level + 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// NAV CHILD COMPONENT
// ============================================================
function NavChild({ item, currentPath, onNavigate, delay, level = 1 }) {
  const [hovered, setHovered] = useState(false);
  const isActive = item.path === currentPath;

  return (
    <div
      className="nav-item-enter"
      style={{
        ...styles.navChild,
        ...(hovered && !isActive ? styles.navChildHover : {}),
        ...(isActive ? styles.navChildActive : {}),
        animationDelay: `${delay}ms`,
        paddingLeft: 20 + (level * 12),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(item.path)}
    >
      <span style={{
        ...styles.navChildDot,
        ...(isActive ? { background: THEME.accent } : {}),
      }} />
      {item.label}
    </div>
  );
}

// ============================================================
// SIDEBAR COMPONENT
// ============================================================
function Sidebar({ currentPath, onNavigate, context, collapsed }) {
  // ✅ Start with all sections collapsed - will auto-expand based on current path
  const [expandedSections, setExpandedSections] = useState([]);
  const [userHovered, setUserHovered] = useState(false);
  const [specsHovered, setSpecsHovered] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const { userName, userAvatar, userEmail, isAuthenticated, isOperator, isProducer } = useAuth();
  
  // Combine nav items based on role
  const navItems = React.useMemo(() => {
    // Producers: uploaders only
    if (isProducer) {
      return PRODUCER_NAV_ITEMS;
    }
    // Operators: everything + operator tools
    if (isOperator) {
      return [...NAV_ITEMS, ...OPERATOR_NAV_ITEMS];
    }
    // Club users: uploaders + MatchFlow
    return NAV_ITEMS;
  }, [isOperator, isProducer]);

  // ✅ Auto-expand sections based on current path
  useEffect(() => {
    if (!currentPath || currentPath === "/") return;
    
    const sectionsToExpand = [];
    
    // Check if current path is under Upload Content
    if (currentPath.startsWith("/uploads/")) {
      sectionsToExpand.push("upload-content");
      
      // Check which venue
      if (currentPath.includes("/marvel/") || currentPath.includes("/commercial/marvel")) {
        sectionsToExpand.push("marvel-uploads");
      } else if (currentPath.includes("/mcg/") || currentPath.includes("/commercial/mcg")) {
        sectionsToExpand.push("mcg-uploads");
      }
    }
    
    // Check if current path is under ControlFlow
    if (currentPath.startsWith("/operator/")) {
      sectionsToExpand.push("operator-portal");
    }
    
    if (sectionsToExpand.length > 0) {
      setExpandedSections(prev => {
        const combined = new Set([...prev, ...sectionsToExpand]);
        return Array.from(combined);
      });
    }
  }, [currentPath]);

  const handleToggle = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Get display name - prefer user's name from auth, fall back to club name
  const displayName = userName || context.submitterName;
  const displayEmail = userEmail || `venueflow@${context.submitterShortName.toLowerCase()}.com.au`;

  return (
    <div 
      style={{
        ...styles.sidebar,
        ...(collapsed ? styles.sidebarCollapsed : {}),
      }}
    >
        {/* Glow effect */}
        <div style={styles.sidebarGlow} />

        {/* Logo - Clickable to go home */}
        <div style={styles.logoSection}>
          <div 
            style={{ ...styles.logoWrapper, cursor: "pointer" }}
            onClick={() => onNavigate("/")}
            title="Go to Home"
          >
            <img 
              src="/lumen8-logo.png" 
              alt="Lumen8" 
              style={styles.logoImage}
            />
          </div>
          <div 
            style={{ ...styles.logoSub, cursor: "pointer" }}
            onClick={() => onNavigate("/")}
            title="Go to Home"
          >
            VenueFlow
          </div>

          {/* Club Badge */}
          <div style={styles.clubBadge}>
            <div style={styles.clubLogo}>
              {context.submitterShortName}
            </div>
            <div style={styles.clubInfo}>
              <div style={styles.clubName}>{context.submitterName}</div>
              <div style={styles.clubType}>{context.submitterType}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {navItems.map((item, idx) => {
            if (item.type === "divider") {
              return <div key={`divider-${idx}`} style={styles.divider} />;
            }
            return (
              <NavItem
                key={item.id}
                item={item}
                currentPath={currentPath}
                onNavigate={onNavigate}
                expanded={expandedSections.includes(item.id)}
                onToggle={handleToggle}
                expandedSections={expandedSections}
              />
            );
          })}
        </nav>

        {/* Specs Button */}
        <div style={styles.footer}>
          <button
            style={{
              ...styles.specsButton,
              ...(specsHovered ? styles.specsButtonHover : {}),
            }}
            onMouseEnter={() => setSpecsHovered(true)}
            onMouseLeave={() => setSpecsHovered(false)}
            onClick={() => window.open("https://lumen8.com.au/", "_blank")}
          >
            <span style={{ width: 18, height: 18 }}>{Icons.specs}</span>
            Lumen8 Specifications
          </button>
        </div>
        
        {/* Match Lock Toggle - Only for operators */}
        {isOperator && <MatchLockToggle />}

        {/* Maintenance Toggle - Only for operators */}
        {isOperator && <MaintenanceToggle />}

        {/* Switch Portal - For operators and producers */}
        {(isOperator || isProducer) && (
          <div style={{ padding: '4px 16px 8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: currentPath === '/hub' ? 'rgba(47, 214, 181, 0.1)' : 'transparent',
                border: `1px solid ${currentPath === '/hub' ? 'rgba(47, 214, 181, 0.2)' : 'transparent'}`,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => onNavigate("/hub")}
              title="Switch between club portals"
              onMouseEnter={(e) => {
                if (currentPath !== '/hub') {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                  e.currentTarget.style.borderColor = THEME.border;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPath !== '/hub') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={currentPath === '/hub' ? '#2fd6b5' : THEME.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" />
              </svg>
              <span style={{ 
                fontSize: 12, 
                fontWeight: 500,
                color: currentPath === '/hub' ? '#2fd6b5' : THEME.textMuted,
              }}>
                Switch Portal
              </span>
            </div>
          </div>
        )}

        {/* User section */}
        <div
          style={{
            ...styles.userSection,
            ...(userHovered ? styles.userSectionHover : {}),
          }}
          onMouseEnter={() => setUserHovered(true)}
          onMouseLeave={() => setUserHovered(false)}
          onClick={() => isAuthenticated && setShowUserSettings(true)}
          title={isAuthenticated ? "Click to manage your profile" : ""}
        >
          <div style={{
            ...styles.avatar,
            ...(userAvatar ? { padding: 0, overflow: "hidden" } : {}),
          }}>
            {userAvatar ? (
              <img 
                src={userAvatar} 
                alt="" 
                style={{ width: "100%", height: "100%", objectFit: "cover" }} 
              />
            ) : (
              getInitials(displayName)
            )}
          </div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{displayName}</div>
            <div style={styles.userEmail}>{displayEmail}</div>
          </div>
          {isAuthenticated && (
            <span style={{ 
              fontSize: 11, 
              color: THEME.textMuted,
              padding: "4px 8px",
              background: THEME.bgCard,
              borderRadius: 4,
            }}>
              ⚙️
            </span>
          )}
        </div>

        {/* User Settings Modal */}
        <UserSettingsModal
          isOpen={showUserSettings}
          onClose={() => setShowUserSettings(false)}
        />
    </div>
  );
}

// ============================================================
// ENHANCED WELCOME SCREEN WITH STATS, TIPS & SUPPORT
// ============================================================
function WelcomeScreen({ onNavigate }) {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredStat, setHoveredStat] = useState(null);
  const [stats, setStats] = useState({ assets: 0, recentUploads: 0, loading: true });
  const [animatedStats, setAnimatedStats] = useState({ assets: 0, recentUploads: 0 });
  const [fixtures, setFixtures] = useState([]);
  const [recentUploads, setRecentUploads] = useState([]);
  const [pipelineStats, setPipelineStats] = useState({ newSubmission: 0, rejected: 0, approved: 0 });
  const { userName } = useAuth();

  // Fetch stats and fixtures on mount
  useEffect(() => {
    async function fetchStats() {
      try {
        const { fetchAllAssets } = await import("./airtable");
        const assets = await fetchAllAssets();
        
        // Count recent uploads (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        let recentCount = 0;
        
        // Convert Map to array and sort by createdTime
        const assetsArray = Array.from(assets.values());
        assetsArray.forEach((asset) => {
          if (asset.createdTime) {
            const created = new Date(asset.createdTime);
            if (created >= sevenDaysAgo) recentCount++;
          }
        });
        
        // Get 3 most recent uploads
        const sortedAssets = assetsArray
          .filter(a => a.createdTime)
          .sort((a, b) => new Date(b.createdTime) - new Date(a.createdTime))
          .slice(0, 3);
        setRecentUploads(sortedAssets);
        
        setStats({ assets: assets.size, recentUploads: recentCount, loading: false });
      } catch (err) {
        console.warn("Failed to fetch stats:", err);
        setStats({ assets: 0, recentUploads: 0, loading: false });
      }
    }
    
    async function fetchFixtures() {
      try {
        const { fetchUpcomingFixtures } = await import("./airtable");
        const upcomingFixtures = await fetchUpcomingFixtures();
        setFixtures(upcomingFixtures);
      } catch (err) {
        console.warn("Failed to fetch fixtures:", err);
        // Fallback to view config if fixtures table fails
        try {
          const { fetchAppViewsConfig } = await import("./airtable");
          const views = await fetchAppViewsConfig();
          const fixtureViews = views.filter(v => 
            v.viewName && 
            !v.viewName.toLowerCase().includes('team list')
          );
          setFixtures(fixtureViews);
        } catch (e) {
          console.warn("Fallback fetch also failed:", e);
        }
      }
    }
    
    async function fetchPipelineStats() {
      try {
        const { fetchContentPipelineStats } = await import("./services/operatorAirtable");
        const pStats = await fetchContentPipelineStats();
        setPipelineStats(pStats);
      } catch (err) {
        console.warn("Failed to fetch pipeline stats:", err);
        // Keep default zeros
      }
    }
    
    fetchStats();
    fetchFixtures();
    fetchPipelineStats();
  }, []);

  // Animate stats counting up
  useEffect(() => {
    if (stats.loading) return;
    
    const duration = 1500;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedStats({
        assets: Math.round(stats.assets * easeOut),
        recentUploads: Math.round(stats.recentUploads * easeOut),
      });
      
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, [stats]);

  const cards = [
    {
      id: "upload",
      icon: "stadium",
      title: "Upload Content",
      desc: "Submit assets to venue displays",
      path: "/uploads/marvel/screens",
    },
    {
      id: "gallery",
      icon: "gallery",
      title: "MatchFlow Gallery",
      desc: "Browse and manage your uploaded content library",
      path: "/ingestflow",
    },
    {
      id: "matchflow",
      icon: "play",
      title: "Launch MatchFlow",
      desc: "Manage content allocation and scheduling",
      path: "/matchflow",
    },
  ];

  const statsData = [
    {
      id: "assets",
      value: stats.loading ? "..." : animatedStats.assets.toLocaleString(),
      label: "Total Assets in Library",
      color: "#2fd6b5",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
    },
    {
      id: "venues",
      value: "2",
      label: "Active Venues",
      color: "#84cc16",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      id: "recent",
      value: stats.loading ? "..." : animatedStats.recentUploads.toLocaleString(),
      label: "New Assets in the Last 7 Days",
      color: "#f59e0b",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
    },
  ];

  const gettingStartedTips = [
    {
      icon: "📁",
      title: "File Naming Conventions",
      desc: "When uploading content, please follow the naming format ie., [Asset_Name]_MRV_FOP-1, as outlined on the Lumen8 Specs website, to avoid content being rejected"
    },
    {
      icon: "🔢",
      title: "Lumen8 Asset IDs",
      desc: "Your Lumen8 Content ID begins with a three-letter club acronym followed by a unique number (e.g. STK_34893). Please reference this ID when communicating with the Lumen8 team about any content enquiries."
    },
    {
      icon: "📐",
      title: "Check Specs First",
      desc: "Always verify dimensions and format requirements before uploading"
    },
    {
      icon: "⏱️",
      title: "Upload Early",
      desc: "Submit content 5+ days before match day for review and approval"
    },
  ];

  const supportContacts = [
    { initials: "BO", name: "Bridget O'Grady", role: "Content Delivery Manager", email: "Bridget.Ogrady@lumen8.com.au" },
    { initials: "LB", name: "Laura Barden", role: "Production Coordinator", email: "Laura.barden@lumen8.com.au" },
    { initials: "RZ", name: "Ruby Zogopoulos", role: "Production Coordinator", email: "Ruby.zogopoulos@lumen8.com.au" },
    { initials: "CS", name: "Connected Stadiums", role: "IPTV Support", email: "connectedstadiums@lumen8.com.au" },
  ];

  const welcomeStyles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "32px 40px 60px",
      overflowY: "auto",
      height: "100%",
    },
    glow: {
      position: "fixed",
      top: "5%",
      left: "50%",
      transform: "translateX(-50%)",
      width: 1000,
      height: 600,
      background: "radial-gradient(ellipse, rgba(47, 214, 181, 0.05) 0%, transparent 60%)",
      pointerEvents: "none",
      zIndex: 0,
    },
    header: {
      textAlign: "center",
      marginBottom: 36,
      position: "relative",
      zIndex: 1,
    },
    greeting: {
      fontSize: 13,
      color: THEME.accent,
      fontWeight: 600,
      marginBottom: 6,
      letterSpacing: "2px",
      textTransform: "uppercase",
    },
    title: {
      fontSize: 38,
      fontWeight: 700,
      color: THEME.textPrimary,
      letterSpacing: "-1px",
      marginBottom: 10,
    },
    titleAccent: {
      background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    subtitle: {
      fontSize: 15,
      color: THEME.textSecondary,
      maxWidth: 520,
      lineHeight: 1.6,
      margin: "0 auto",
    },
    section: {
      width: "100%",
      maxWidth: 960,
      marginBottom: 32,
      position: "relative",
      zIndex: 1,
    },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      color: THEME.textMuted,
      textTransform: "uppercase",
      letterSpacing: "1.5px",
      marginBottom: 14,
    },
    cardsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 16,
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 16,
    },
    statCard: (color, isHovered) => ({
      background: isHovered ? `${color}10` : THEME.bgCard,
      border: `1px solid ${isHovered ? `${color}40` : THEME.border}`,
      borderRadius: 14,
      padding: 20,
      display: "flex",
      alignItems: "center",
      gap: 16,
      transition: "all 0.25s ease",
      cursor: "default",
      position: "relative",
      overflow: "hidden",
    }),
    statGlow: (color) => ({
      position: "absolute",
      top: -20,
      right: -20,
      width: 80,
      height: 80,
      background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
      pointerEvents: "none",
    }),
    statIcon: (color) => ({
      width: 48,
      height: 48,
      borderRadius: 12,
      background: `${color}15`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: color,
      flexShrink: 0,
    }),
    statValue: {
      fontSize: 32,
      fontWeight: 700,
      color: THEME.textPrimary,
      lineHeight: 1,
      fontFamily: "'JetBrains Mono', monospace",
    },
    statLabel: {
      fontSize: 12,
      color: THEME.textMuted,
      marginTop: 4,
    },
    bottomRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 20,
      width: "100%",
    },
    supportCard: {
      background: THEME.bgCard,
      border: `1px solid ${THEME.border}`,
      borderRadius: 14,
      padding: 20,
    },
    supportTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: THEME.textPrimary,
      marginBottom: 14,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    contactItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 0",
      borderBottom: `1px solid ${THEME.border}`,
    },
    contactItemLast: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 0",
    },
    contactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 8,
      background: "linear-gradient(135deg, #2fd6b5, #84cc16)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 600,
      color: "#111",
      flexShrink: 0,
    },
    contactName: {
      fontSize: 13,
      fontWeight: 500,
      color: THEME.textPrimary,
    },
    contactRole: {
      fontSize: 11,
      color: THEME.textMuted,
    },
    contactEmail: {
      fontSize: 11,
      color: THEME.accent,
      textDecoration: "none",
      marginLeft: "auto",
      opacity: 0.8,
      transition: "opacity 0.15s",
    },
    tipItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "10px 0",
      borderBottom: `1px solid ${THEME.border}`,
    },
    tipItemLast: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "10px 0",
    },
    tipIcon: {
      fontSize: 18,
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: THEME.bgHover,
      borderRadius: 8,
      flexShrink: 0,
    },
    tipTitle: {
      fontSize: 13,
      fontWeight: 500,
      color: THEME.textPrimary,
      marginBottom: 2,
    },
    tipDesc: {
      fontSize: 11,
      color: THEME.textMuted,
      lineHeight: 1.4,
    },
  };

  return (
    <div style={welcomeStyles.container}>
      <div style={welcomeStyles.glow} />
      
      {/* Welcome Hero - TOP */}
      <WelcomeHero userName={userName} />

      {/* Quick Actions */}
      <div style={{ ...welcomeStyles.section, maxWidth: 1100 }}>
        <div style={welcomeStyles.sectionTitle}>Quick Actions</div>
        <div style={welcomeStyles.cardsGrid}>
          {cards.map((card) => (
            <div
              key={card.id}
              style={{
                ...styles.welcomeCard,
                ...(hoveredCard === card.id ? styles.welcomeCardHover : {}),
              }}
              onMouseEnter={() => setHoveredCard(card.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => onNavigate(card.path)}
            >
              <div style={styles.welcomeCardIcon}>
                <span style={{ width: 22, height: 22 }}>{Icons[card.icon]}</span>
              </div>
              <div style={styles.welcomeCardTitle}>{card.title}</div>
              <div style={styles.welcomeCardDesc}>{card.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Overview */}
      <div style={{ ...welcomeStyles.section, maxWidth: 1100 }}>
        <div style={welcomeStyles.sectionTitle}>Content Overview</div>
        <div style={welcomeStyles.statsGrid}>
          {statsData.map((stat) => (
            <div 
              key={stat.id} 
              style={welcomeStyles.statCard(stat.color, hoveredStat === stat.id)}
              onMouseEnter={() => setHoveredStat(stat.id)}
              onMouseLeave={() => setHoveredStat(null)}
            >
              <div style={welcomeStyles.statGlow(stat.color)} />
              <div style={welcomeStyles.statIcon(stat.color)}>{stat.icon}</div>
              <div>
                <div style={welcomeStyles.statValue}>{stat.value}</div>
                <div style={welcomeStyles.statLabel}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dashboard Widgets - Next Match, Recent Uploads, Pipeline */}
      <div style={{ ...welcomeStyles.section, maxWidth: 1100 }}>
        <WelcomeDashboard 
          fixtures={fixtures}
          recentUploads={recentUploads}
          pipelineStats={pipelineStats}
          onNavigate={onNavigate}
        />
      </div>

      {/* Getting Started Section */}
      <div style={{ ...welcomeStyles.section, maxWidth: 1100 }}>
        <div style={welcomeStyles.sectionTitle}>Getting Started</div>
        <div style={welcomeStyles.bottomRow}>
          {/* Getting Started Tips */}
          <div style={welcomeStyles.supportCard}>
            <div style={welcomeStyles.supportTitle}>
              <span>💡</span> Tips
            </div>
            {gettingStartedTips.map((tip, idx) => (
              <div 
                key={idx} 
                style={idx === gettingStartedTips.length - 1 ? welcomeStyles.tipItemLast : welcomeStyles.tipItem}
              >
                <div style={welcomeStyles.tipIcon}>{tip.icon}</div>
                <div>
                  <div style={welcomeStyles.tipTitle}>{tip.title}</div>
                  <div style={welcomeStyles.tipDesc}>{tip.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Support Contacts */}
          <div style={welcomeStyles.supportCard}>
            <div style={welcomeStyles.supportTitle}>
              <span>💬</span> Lumen8 Support
            </div>
            <div style={{
              fontSize: 12,
              color: THEME.textMuted,
              lineHeight: 1.5,
              marginBottom: 14,
            }}>
              Need a hand getting started? Here are your Lumen8 support contacts for VenueFlow.
            </div>
            {supportContacts.map((contact, idx) => (
              <div 
                key={idx}
                style={idx === supportContacts.length - 1 ? welcomeStyles.contactItemLast : welcomeStyles.contactItem}
              >
                <div style={welcomeStyles.contactAvatar}>{contact.initials}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={welcomeStyles.contactName}>{contact.name}</div>
                  <div style={welcomeStyles.contactRole}>{contact.role}</div>
                </div>
                <a 
                  href={`mailto:${contact.email}`}
                  style={welcomeStyles.contactEmail}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0.8"}
                >
                  Email →
                </a>
              </div>
            ))}
            
            {/* Version info */}
            <div style={{ 
              marginTop: 14, 
              paddingTop: 14, 
              borderTop: `1px solid ${THEME.border}`,
              fontSize: 10,
              color: THEME.textDim,
              display: "flex",
              justifyContent: "space-between",
            }}>
              <span>Lumen8 VenueFlow Version 3.0 Deployed</span>
              <span>Season 2026</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PAGE ROUTER
// ============================================================
function PageRouter({ currentPath, onNavigate, isMatchFlow }) {
  const { isProducer } = useAuth();
  
  // Producers: block MatchFlow Builder, operator routes, admin (but allow Hub and IngestFlow)
  if (isProducer && (isMatchFlow || currentPath === "/matchflow" || currentPath.startsWith("/operator") || currentPath === "/admin" || currentPath === "/commercial-qa")) {
    return (
      <div className="page-enter" key="access-restricted" style={styles.mainContent}>
        <WelcomeScreen onNavigate={onNavigate} />
      </div>
    );
  }
  
  // If we're in MatchFlow mode, render the actual MatchFlow app
  if (isMatchFlow) {
    return <MatchFlowApp />;
  }

  const routes = {
    "/": <WelcomeScreen onNavigate={onNavigate} />,
    
    // Marvel Uploads
    "/uploads/marvel/screens": <MarvelScreensPage />,
    "/uploads/marvel/led-ribbons": <MarvelLEDPage />,
    "/uploads/marvel/iptv": <MarvelIPTVPage />,
    "/uploads/marvel/tvc": <MarvelTVCPage />,
    
    // MCG Uploads
    "/uploads/mcg/bigscreen": <MCGBigScreenPage />,
    "/uploads/mcg/iptv": <MCGIPTVPage />,
    "/uploads/mcg/led-ribbons": <MCGLEDPage />,
    "/uploads/mcg/tvc": <MCGTVCPage />,
    
    // Commercial Entitlements
    "/uploads/commercial/mcg-led": <MCGCommercialLEDPage />,
    "/uploads/commercial/marvel-led": <MarvelCommercialLEDPage />,
    "/uploads/commercial/mcg-iptv": <MCGCommercialIPTVPage />,
    "/uploads/commercial/marvel-iptv": <MarvelCommercialIPTVPage />,
    
    // IngestFlow Gallery
    "/ingestflow": <IngestFlowGalleryPage />,
    
    // ControlFlow (Lumen8 staff only)
    "/operator/cg": (
      <OperatorContextProvider>
        <OperatorDashboard />
      </OperatorContextProvider>
    ),
    "/operator/ribbon": (
      <OperatorContextProvider>
        <OperatorDashboard />
      </OperatorContextProvider>
    ),
    "/operator/tvc": (
      <OperatorContextProvider>
        <OperatorDashboard />
      </OperatorContextProvider>
    ),
    "/operator/iptv": (
      <OperatorContextProvider>
        <OperatorDashboard />
      </OperatorContextProvider>
    ),
    "/operator/led": (
      <OperatorContextProvider>
        <OperatorDashboard />
      </OperatorContextProvider>
    ),
    
    // Password Manager (Lumen8 staff only)
    "/admin": <AdminPage />,
    
    // Commercial QA (Lumen8 staff only)
    "/commercial-qa": <CommercialQAView />,
    
    // Portal Hub (Lumen8 staff and producers with allowed_clubs)
    "/hub": <PortalHub />,
  };

  return (
    <div className="page-enter" key={currentPath} style={styles.mainContent}>
      {routes[currentPath] || <NotFound path={currentPath} />}
    </div>
  );
}

// ============================================================
// MAINTENANCE WRAPPER - Shows overlay for non-operators when maintenance is on
// ============================================================
function MaintenanceWrapper({ children }) {
  const { isMaintenanceMode } = useMaintenanceMode();
  const { isOperator } = useAuth();
  
  // Show maintenance overlay for non-operators when maintenance mode is on
  if (isMaintenanceMode && !isOperator) {
    return <MaintenanceOverlay />;
  }
  
  return children;
}
function NotFound({ path }) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      padding: 40,
      textAlign: "center",
    }}>
      <h1 style={{ fontSize: 32, fontWeight: 700, color: THEME.textPrimary, marginBottom: 12 }}>Page Not Found</h1>
      <p style={{ fontSize: 16, color: THEME.textSecondary }}>
        The page <code style={{ color: THEME.accent }}>{path}</code> doesn't exist.
      </p>
    </div>
  );
}

// ============================================================
// APP SHELL CONTENT
// ============================================================
function AppShellContent() {
  const context = useVFContext();
  const [currentPath, setCurrentPath] = useState("/");

  // Determine if we're in MatchFlow mode (sidebar should be collapsed)
  const isMatchFlow = currentPath === "/matchflow";

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path);
    window.history.pushState({}, "", path);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    if (path && path !== "/") {
      setCurrentPath(path);
    }
  }, []);

  return (
    <div style={styles.shell}>
      {/* Sidebar - always visible */}
      <Sidebar
        currentPath={currentPath}
        onNavigate={handleNavigate}
        context={context}
        collapsed={false}
      />
      
      <main style={styles.main}>
        <PageRouter 
          currentPath={currentPath} 
          onNavigate={handleNavigate}
          isMatchFlow={isMatchFlow}
        />
      </main>
      
      <UpdateBanner />
    </div>
  );
}

// ============================================================
// AUTH GATE - Shows login if not authenticated
// ============================================================
function AuthGate({ children }) {
  const { 
    isAuthenticated, 
    loading, 
    needsProfileComplete, 
    mustChangePassword,
    accessDenied, 
    userEmail, 
    userClub, 
    appClub,
    signOut,
    assignUserClub,
  } = useAuth();

  // If Supabase isn't configured, skip auth entirely
  if (!isSupabaseConfigured()) {
    return children;
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: THEME.bg,
        color: THEME.textSecondary,
        fontFamily: "'Space Grotesk', sans-serif",
      }}>
        Loading...
      </div>
    );
  }

  // User is logged in but doesn't have access to this club
  if (accessDenied) {
    // Check if user has no club assigned (legacy user or first-time setup)
    const hasNoClub = !userClub;
    
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: THEME.bg,
        padding: 24,
      }}>
        <div style={{
          background: THEME.bgElevated,
          borderRadius: 20,
          padding: "48px 40px",
          width: "100%",
          maxWidth: 450,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
          border: `1px solid ${THEME.border}`,
          textAlign: "center",
        }}>
          {/* Warning Icon */}
          <div style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: hasNoClub ? "rgba(47, 214, 181, 0.15)" : "rgba(239, 68, 68, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}>
            {hasNoClub ? (
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#2fd6b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            ) : (
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            )}
          </div>

          <h2 style={{
            fontSize: 22,
            fontWeight: 600,
            color: THEME.textPrimary,
            fontFamily: "'Space Grotesk', sans-serif",
            marginBottom: 12,
          }}>
            {hasNoClub ? "Welcome!" : "Access Denied"}
          </h2>

          {hasNoClub ? (
            <>
              <p style={{
                fontSize: 14,
                color: THEME.textSecondary,
                lineHeight: 1.6,
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: 16,
              }}>
                Your account (<strong style={{ color: THEME.textPrimary }}>{userEmail}</strong>) hasn't been assigned to a club yet.
              </p>

              <p style={{
                fontSize: 14,
                color: THEME.textSecondary,
                lineHeight: 1.6,
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: 24,
              }}>
                Would you like to register with the <strong style={{ color: THEME.accent }}>{appClub}</strong> portal?
              </p>

              <button
                onClick={async () => {
                  await assignUserClub();
                  window.location.reload();
                }}
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  fontSize: 15,
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #2fd6b5 0%, #cdde25 100%)",
                  color: THEME.bg,
                  border: "none",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: "'Space Grotesk', sans-serif",
                  transition: "transform 0.15s, box-shadow 0.2s",
                  marginBottom: 12,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 0 60px rgba(47, 214, 181, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Yes, Register with {appClub}
              </button>

              <button
                onClick={() => signOut()}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  fontSize: 14,
                  fontWeight: 500,
                  background: "transparent",
                  color: THEME.textSecondary,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <p style={{
                fontSize: 14,
                color: THEME.textSecondary,
                lineHeight: 1.6,
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: 8,
              }}>
                Your account (<strong style={{ color: THEME.textPrimary }}>{userEmail}</strong>) is registered to{" "}
                <strong style={{ color: THEME.accent }}>{userClub}</strong>.
              </p>

              <p style={{
                fontSize: 14,
                color: THEME.textSecondary,
                lineHeight: 1.6,
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: 24,
              }}>
                You cannot access the <strong style={{ color: THEME.accent }}>{appClub}</strong> portal.
              </p>

              <div style={{
                padding: "16px",
                background: THEME.bgCard,
                borderRadius: 12,
                marginBottom: 24,
              }}>
                <p style={{
                  fontSize: 13,
                  color: THEME.textMuted,
                  fontFamily: "'Space Grotesk', sans-serif",
                  margin: 0,
                }}>
                  If you believe this is an error, please contact your Lumen8 coordinator.
                </p>
              </div>

              <button
                onClick={() => signOut()}
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  fontSize: 15,
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #2fd6b5 0%, #cdde25 100%)",
                  color: THEME.bg,
                  border: "none",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: "'Space Grotesk', sans-serif",
                  transition: "transform 0.15s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 0 60px rgba(47, 214, 181, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Sign Out & Switch Account
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Authenticated - assign club if not already assigned (non-Lumen8 users)
  // This runs once when user first completes profile
  if (needsProfileComplete) {
    // Will assign club after profile is completed
  }

  // Authenticated but needs to complete profile (set name)
  // Show modal overlay on top of the app
  // WelcomeTour shows after profile is complete (first time only)
  // ChangePasswordModal shows if user logged in with temp password
  // Hub mode: skip tour, help widget, and profile complete — those belong on club portals
  const isHubMode = process.env.REACT_APP_MODE === "hub";
  
  return (
    <>
      {children}
      {!isHubMode && mustChangePassword && <ChangePasswordModal />}
      {!isHubMode && !mustChangePassword && needsProfileComplete && <ProfileCompleteModal onComplete={assignUserClub} />}
      {!isHubMode && !mustChangePassword && !needsProfileComplete && <WelcomeTour />}
      {!isHubMode && !mustChangePassword && !needsProfileComplete && <ChatWidget />}
      {!isHubMode && !mustChangePassword && !needsProfileComplete && <HelpWidget />}
    </>
  );
}

// ============================================================
// APP MODE
// ============================================================
const APP_MODE = process.env.REACT_APP_MODE || "club";

// ============================================================
// APP SHELL WITH PROVIDERS
// ============================================================
export function AppShell() {
  // Hub mode: minimal provider tree, just auth + hub page
  if (APP_MODE === "hub") {
    return (
      <ThemeProvider>
        <AuthProvider>
          <AuthGate>
            <HubPage />
          </AuthGate>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Club mode: full app with all providers
  return (
    <ThemeProvider>
      <AuthProvider>
        <PresenceProvider>
          <VFContextProvider>
            <MaintenanceProvider>
              <MatchLockProvider>
              <AuthGate>
                <MaintenanceWrapper>
                  <AppShellContent />
                </MaintenanceWrapper>
              </AuthGate>
              </MatchLockProvider>
            </MaintenanceProvider>
          </VFContextProvider>
        </PresenceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default AppShell;
